"use client"

import * as React from "react"
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/primitives/drawer"
import { cn } from "@/primitives/utils"

/*
 * beacon: the Sheet is a Drawer from a side of the screen, so it drags shut toward its edge, stands
 * inside the safe areas, and closes on Back. The side is the root's (`side`), since the drawer's
 * swipe is decided there; upstream put it on the content.
 */

const swipeOf = { top: "up", right: "right", bottom: "down", left: "left" } as const

type Side = keyof typeof swipeOf

function Sheet({
  side = "right",
  ...props
}: React.ComponentProps<typeof Drawer> & { side?: Side }) {
  return <Drawer data-slot="sheet" swipeDirection={swipeOf[side]} {...props} />
}

function SheetTrigger({ ...props }: React.ComponentProps<typeof DrawerTrigger>) {
  return <DrawerTrigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({ ...props }: React.ComponentProps<typeof DrawerClose>) {
  return <DrawerClose data-slot="sheet-close" {...props} />
}

function SheetContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: Omit<React.ComponentProps<typeof DrawerContent>, "size">) {
  return (
    <DrawerContent
      data-slot="sheet-content"
      showCloseButton={showCloseButton}
      className={cn(
        // beacon: a side sheet is 28rem wide, never closer than 3rem to the far edge, with a subtle
        // edge on the side it opens from.
        "border-border-subtle data-[swipe-axis=x]:w-md data-[swipe-axis=x]:max-w-[calc(100%-3rem)] data-[swipe-direction=left]:border-r data-[swipe-direction=right]:border-l data-[swipe-direction=up]:border-b",
        className,
      )}
      {...props}
    >
      {children}
    </DrawerContent>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <DrawerHeader
      data-slot="sheet-header"
      // beacon: as DialogHeader, clear of the close button on the right.
      className={cn("pt-5 pr-12", className)}
      {...props}
    />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <DrawerFooter
      data-slot="sheet-footer"
      // beacon: as DialogFooter, at the end of the sheet.
      className={cn("justify-end *:flex-none", className)}
      {...props}
    />
  )
}

function SheetTitle({ ...props }: React.ComponentProps<typeof DrawerTitle>) {
  return <DrawerTitle data-slot="sheet-title" {...props} />
}

function SheetDescription({ ...props }: React.ComponentProps<typeof DrawerDescription>) {
  return <DrawerDescription data-slot="sheet-description" {...props} />
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
